import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { InvoiceDetail, TransactionDetail } from '../../../../api/useFreight/types'
import {
  getActionsColumnDef,
  getAmountColumnDef,
  getBOLColumnDef,
  getDestinationsColumnDef,
  getLineItemTypeColumnDef,
  getOriginsColumnDef,
  getProductGroupColumnDef,
  getProductNameColumnDef,
  getRateColumnDef,
  getStatusColumnDef,
  getSubtypeColumnDef,
  getTotalColumnDef,
  getUomColumnDef,
} from './utils'
import { StatusCellRenderer } from '.'

/**
 * Column definitions for the invoice detail line items grid
 *
 * @param hideActions - Whether to hide the actions column
 * @param hideOrderNumber - Whether to hide the order number column
 * @param hideStatus - Whether to hide the status column
 * @returns Column definitions for the detail line items grid
 */
export const getDetailLineItemColumns = (
  hideActions?: boolean,
  hideOrderNumber?: boolean,
  hideStatus?: boolean
): ColDef<TransactionDetail | InvoiceDetail>[] => [
  // Order number column
  {
    field: 'order_number',
    headerName: 'Order',
    initialSort: 'desc',
    hide: hideOrderNumber,
  },

  // Status column
  {
    ...getStatusColumnDef(hideStatus),
  },
  {
    ...getOriginsColumnDef(),
  },
  {
    ...getDestinationsColumnDef(),
  },
  {
    ...getBOLColumnDef(),
  },

  // Product group column
  {
    ...getProductGroupColumnDef(),
  },

  // Product name column
  {
    ...getProductNameColumnDef(),
  },

  // Type column
  {
    ...getLineItemTypeColumnDef(),
  },

  // Subtype column
  {
    ...getSubtypeColumnDef(),
    flex: 2,
  },

  // Rate column
  {
    ...getRateColumnDef(),
  },

  // Amount column
  {
    ...getAmountColumnDef(),
    flex: 0.6,
  },

  // UOM column
  {
    ...getUomColumnDef(),
    flex: 0.6,
  },

  // Total column
  {
    ...getTotalColumnDef(),
  },

  // Actions column
  {
    ...getActionsColumnDef(hideActions),
  },
]

/**
 * Column definitions for the rebill mode of invoice detail line items grid
 *
 * @param hideActions - Whether to hide the actions column
 * @param hideOrderNumber - Whether to hide the order number column
 * @param hideStatus - Whether to hide the status column
 * @returns Column definitions for the rebill mode detail line items grid
 */
export const rebillModeColumns = (
  hideActions?: boolean,
  hideOrderNumber?: boolean,
  hideStatus?: boolean
): ColDef<TransactionDetail | InvoiceDetail>[] => [
  // Order number column
  {
    field: 'order_number',
    headerName: 'Order',
    initialSort: 'desc',
    hide: hideOrderNumber,
  },

  // Status column - with rebill mode
  {
    ...getStatusColumnDef(hideStatus),
    cellRenderer: (params: ICellRendererParams) => <StatusCellRenderer isRebillMode data={params.data} />,
  },
  {
    ...getOriginsColumnDef(),
  },
  {
    ...getDestinationsColumnDef(),
  },
  {
    ...getBOLColumnDef(),
  },

  // Product group column
  {
    ...getProductGroupColumnDef(),
    initialHide: true,
  },

  // Product name column
  {
    ...getProductNameColumnDef(),
  },

  // Type column
  {
    ...getLineItemTypeColumnDef(),
  },

  // Subtype column
  {
    ...getSubtypeColumnDef(),
    flex: 2,
  },

  // Rate column
  {
    ...getRateColumnDef(),
    flex: 0.6,
  },

  // Amount column
  {
    ...getAmountColumnDef(),
    flex: 0.6,
  },

  // UOM column
  {
    ...getUomColumnDef(),
    flex: 0.6,
  },

  // Total column
  {
    ...getTotalColumnDef(),
  },

  // Actions column
  {
    ...getActionsColumnDef(hideActions, true),
    width: 140,
  },
]
